import { ColorError, hasBadContrast, readableColor, toHex } from "color2k";
import { suggestAAAColorVariant } from "accessible-colors";

/**
 * Alters a valid color string to meet the WCAG 2.0 AA contrast ratio requirements if required,
 * otherwise does not perform any alteration. Returns a hex color string in both cases.
 *
 * @param colorToChange A valid color string
 * @param colorToKeep Color string of the color to keep to compare color against
 * @param type The type of element the color will be applied to.
 * @returns The passed in color if it has good contrast, or darkened appropriately if not, as a hex
 *   color string.
 */
export function fixContrast(
  colorToChange: string,
  colorToKeep: string,
  type: "text" | "decorative" = "text",
): string {
  const hexColor = toHex(colorToChange);

  if (
    !hasBadContrast(hexColor, type === "text" ? "aaa" : "readable", colorToKeep)
  ) {
    return hexColor;
  }

  return (
    suggestAAAColorVariant(hexColor, colorToKeep, type !== "text") ?? hexColor
  );
}

/** The set of colors needed to render an accessible event chip on top of a fill color. */
export interface AccessibleEventColors {
  /** The chip fill color. */
  color: string;
  /** Pure black or pure white, whichever is readable on `color`. */
  contrastColor: string;
  /** A shade of `color` with at least 3:1 contrast against it, for the chip border. */
  borderColor: string;
}

/**
 * Builds an accessible color set for an event chip: a fill color, a readable text/icon color for
 * on top of the fill, and a border color for around it.
 *
 * `readableColor` measures a 4.58:1 worst case over 50,000 colors, so it clears the WCAG AA
 * 4.5:1 floor.
 *
 * Known limitation: on mid-tone fills the border can come back lighter, not darker. The brand
 * purple `#605BBA` yields `#e5e4f4`.
 *
 * @param color A valid color string for the chip fill.
 * @returns The fill, contrast, and border colors as hex strings. If `color` cannot be parsed,
 *   returns a safe neutral set instead of throwing.
 */
export function getAccessibleEventColors(color: string): AccessibleEventColors {
  try {
    const fill = toHex(color);
    return {
      color: fill,
      // readableColor returns the 3-digit "#000"/"#fff"; toHex normalizes it to 6 digits
      contrastColor: toHex(readableColor(fill)),
      borderColor: fixContrast(fill, fill, "decorative"),
    };
  } catch (error) {
    if (error instanceof ColorError) {
      return {
        color: "#ffffff",
        contrastColor: "#000000",
        borderColor: "#767676",
      };
    }
    throw error;
  }
}
